import { Check } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import styles from './form.module.css';

export interface Choice<T extends string> {
  value: T;
  label: ReactNode;
}

interface ChoiceGroupProps<T extends string> {
  legend: string;
  hint?: ReactNode;
  error?: string;
  value: T | null;
  options: Array<Choice<T>>;
  onChange: (value: T) => void;
  /**
   * Options that belong in one row (2–3 equal choices): with this many or fewer options
   * they become a segmented control; otherwise they wrap as chips.
   */
  columns?: number;
  disabled?: boolean;
  /** Optional field: tapping the selected option again clears the choice. */
  onClear?: () => void;
  /** @deprecated Chips size to their label; kept so callers need not change. */
  minOptionWidth?: string;
}

/** Radio buttons for a handful of options: chips, or a segmented control for 2–3 choices. */
export function ChoiceGroup<T extends string>({
  legend,
  hint,
  error,
  value,
  options,
  onChange,
  columns,
  disabled,
  onClear,
}: ChoiceGroupProps<T>) {
  const name = useId();
  const segmented = columns !== undefined && options.length <= columns;
  return (
    <fieldset className={styles.fieldset} disabled={disabled}>
      <legend className={styles.label}>{legend}</legend>
      {hint && <p className={styles.hint}>{hint}</p>}
      <div className={segmented ? styles.segmented : styles.choices}>
        {options.map((o) => {
          const selected = o.value === value;
          return (
            <label
              key={o.value}
              className={selected ? `${styles.choice} ${styles.choiceSelected}` : styles.choice}
              onClick={(e) => {
                if (selected && onClear) {
                  e.preventDefault();
                  onClear();
                }
              }}
            >
              <input
                type="radio"
                name={name}
                value={o.value}
                checked={selected}
                onChange={() => onChange(o.value)}
                className="visually-hidden"
              />
              {selected && <Check aria-hidden="true" className={styles.choiceCheck} />}
              <span>{o.label}</span>
            </label>
          );
        })}
      </div>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}
